
import { useState, useEffect } from 'react';

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'partnership',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [captchaError, setCaptchaError] = useState(false);

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaValue('');
    setCaptchaError(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (parseInt(captchaValue) !== captchaQuestion.answer) {
      setCaptchaError(true);
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setCaptchaError(false);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d69etvma728k8ctu2mug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: 'partnership',
          message: '',
        });
        generateCaptcha();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="inline-flex items-center bg-gray-100 rounded-full px-4 py-2 mb-6">
              <i className="ri-mail-send-line text-gray-600 mr-2 w-4 h-4 flex items-center justify-center"></i>
              <span className="text-xs font-medium text-gray-700" style={{ fontFamily: "'Inter', sans-serif" }}>
                Get in Touch
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Let's Connect
            </h2>
            <p className="text-base text-gray-600 mb-10 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              For partnership inquiries, investment opportunities, technical support, or feedback, we'd love to hear from you. Our team is ready to discuss how we can work together.
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-xl text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Email Us
                  </h4>
                  <a href="mailto:it@drixemgroup.com" className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                    it@drixemgroup.com
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-whatsapp-line text-xl text-green-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    WhatsApp
                  </h4>
                  <a href="https://wa.me/60123835600" target="_blank" rel="noopener noreferrer" className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                    +6012-3835600
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-building-line text-xl text-amber-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Headquarters
                  </h4>
                  <p className="text-sm text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Kuala Lumpur, Malaysia
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Your Name *
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm bg-white"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm bg-white"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Subject *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm bg-white"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <option value="partnership">Partnership Inquiry</option>
                  <option value="investment">Investment Opportunity</option>
                  <option value="technical">Technical Support</option>
                  <option value="feedback">Feedback</option>
                  <option value="media">Media & Press</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm resize-none bg-white"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                ></textarea>
                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {formData.message.length}/500 characters
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 border border-gray-200">
                  <span className="text-sm font-medium text-gray-700" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {captchaQuestion.num1} + {captchaQuestion.num2} =
                  </span>
                  <input
                    type="text"
                    value={captchaValue}
                    onChange={(e) => setCaptchaValue(e.target.value)}
                    placeholder="?"
                    required
                    className={`w-16 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm text-center ${captchaError ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>
                <button
                  type="button"
                  onClick={generateCaptcha}
                  className="text-gray-500 hover:text-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line text-xl w-5 h-5 flex items-center justify-center"></i>
                </button>
                {captchaError && (
                  <span className="text-xs text-red-500" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Incorrect answer
                  </span>
                )}
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Thank you for reaching out! We'll get back to you soon.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Something went wrong. Please try again or email us directly.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-teal-600 text-white rounded-full text-sm font-medium hover:bg-teal-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
